/**
 * @file Prompt-building primitives shared with the Cloud Run dispatcher.
 * Renders the lean schema (forms, tables, entities) into the deterministic
 * text block the system prompt embeds, and exposes `buildAgentSystemPrompt`
 * / `systemAgentUserPrompt`. `buildAgenticSystemPrompt` appends the PDF
 * intake guidance only when the tool slice offers `startPdfIntake`. The renderers must stay pure so the same
 * `AgentContext` always produces the same prompt text — that property is
 * what makes prompt caching effective downstream.
 */
import type { AgentContext, AgentContextSchema } from './AgentContext.types';
import type { LeanColumnSummary, LeanFormType, LeanEntity, LeanTableSummary } from './AgentSchema.types';
import type { AgentToolDefinition } from './System.Agents.types';

function renderColumn(col: LeanColumnSummary): string {
    const labelPart = col.label ? ` "${col.label}"` : '';
    if (col.kind === 'field') {
        const dt = col.fieldDataTypeId ? ` :: ${col.fieldDataTypeId}` : '';
        return `    - ${col.typeId}${labelPart}${dt}`;
    }
    const refKind = col.referenceType ?? 'reference';
    const target = col.entityId ? ` → ${col.entityId}${col.entityGroupId ? `/${col.entityGroupId}` : ''}` : '';
    return `    - ${col.typeId}${labelPart} :: ref<${refKind}>${target}`;
}

function renderTables(tables: Record<string, LeanTableSummary>): string[] {
    const ids = Object.keys(tables);
    if (ids.length === 0) return [];
    const lines: string[] = ['Tables:'];
    for (const id of ids) {
        const t = tables[id];
        const label = t.label ? ` "${t.label}"` : '';
        lines.push(`  - ${id}${label}:`);
        if (t.columns.length === 0) {
            lines.push('    (no columns)');
        } else {
            for (const col of t.columns) lines.push(renderColumn(col));
        }
    }
    return lines;
}

function renderFields(fields: Record<string, { shortLabel?: string }>): string[] {
    const ids = Object.keys(fields);
    if (ids.length === 0) return [];
    const lines: string[] = ['Fields:'];
    for (const id of ids) {
        const label = fields[id].shortLabel ? ` "${fields[id].shortLabel}"` : '';
        lines.push(`  - ${id}${label}`);
    }
    return lines;
}

function renderFormTypeRefs(refs: LeanFormType['entityReferences']): string[] {
    if (refs.length === 0) return [];
    const lines = ['Entity references:'];
    for (const r of refs) {
        lines.push(`  - ${r.key} → ${r.entityId}${r.entityGroupId ? `/${r.entityGroupId}` : ''}`);
    }
    return lines;
}

function renderSchema(schema: AgentContextSchema): string {
    const doc = schema.kind === 'formType' ? schema.formType as LeanFormType : schema.entity as LeanEntity;
    const lines: string[] = [];
    lines.push(...renderFields(doc.fields));
    lines.push(...renderFormTypeRefs(doc.entityReferences));
    lines.push(...renderTables(doc.tables));
    if (lines.length === 0) return '';
    const header = schema.kind === 'formType' ? 'Bound form type schema:' : 'Bound entity schema:';
    return `\n${header}\n${lines.join('\n')}`;
}

/**
 * Three-layer system prompt for the in-app agent
 * (React `Agent` component → `runLanguageModel` agentic mode).
 *
 * - `systemAgentSystem`: identity/role. Code constant.
 * - `systemAgentPrompt`: behavior + output-formatting rules. Code constant.
 * - `systemAgentUserPrompt`: tenant-editable extension persisted at
 *   `parameters/aiAgent.userPrompt`; the value below is the default seed.
 *
 * `buildAgentSystemPrompt` concatenates the three layers in order.
 */

export const systemAgentSystem = `You are a Systems Operator.`;

export const systemAgentPrompt = `Output formatting:
- Be brief and to the point, especially in multi-turn conversations. Use short sentences and paragraphs.
- Write clearly, be precise and professional.
- Format structured or comparative information as bulleted or nested lists.
- Do not render MD or HTML tables.
- For key-value pairs, use a list with bold labels: "- **Field:** value".
- For grouped data, nest items under a parent bullet.
- Use headings (##, ###) to separate sections when responses are long.
- Refer to objects by their name or label, never by their ID. IDs are not human-readable. Use IDs only when calling tools that require them.`;

export const systemAgentUserPrompt = ``;

export function buildAgentSystemPrompt(userPrompt: string): string {
    return [systemAgentSystem, systemAgentPrompt, userPrompt]
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .join('\n\n');
}

export const EXTRACTION_TOOL_NAMES: ReadonlySet<string> = new Set([
    'extractFromFile',
    'startPdfIntake',
    'readExtractedFile',
    'getSchema',
    'createForm',
    'proposeImport',
    'applyToRecords',
    'search',
    'applyToDocument',
    'getTableRows',
]);

export const AGENTIC_NO_TOOL_NUDGE =
    'You must call the "finish" tool to signal completion. Please call it now with an appropriate status and summary.';

const AGENTIC_EXTRACTION_GUIDANCE = `

For file extraction (every file tool takes the file's fileId, surfaced as <file_id> — a durable handle that works this turn and every later turn):
- extractFromFile(fileId) → parses the file; returns description, chunks, and labels. The parse is persisted to the file's folder.
- readExtractedFile(fileId) → re-read an ALREADY-parsed file, cheap, never re-parses: omit both chunkId/groupId for the catalog of chunks; pass a chunkId (one fragment) or groupId (a whole section, e.g. a table spanning pages) to page table rows (offset/limit), read a text section, or filter rows by tag (value/summary/junk). Use this to look again — do NOT call extractFromFile to re-read.
- Re-extract (extractFromFile on the same fileId) ONLY to retry data that came back corrupted. If the built-in parser cannot read a spreadsheet/CSV/JSON/XML/TXT file it returns a clear error (it never silently returns image descriptions) — report that error to the user; do not paper over it.
- PDF/DOCX/PPTX go to the document-AI extractor, which OCRs scanned and image-only pages, so a scanned document still returns its text and tables. If it comes back empty, the file is genuinely empty or unreadable — report that to the user; there is no alternate extractor to escalate to.
- Emails (eml/msg): the body and attachment list are already in the user prompt. For each attachment that looks relevant, call extractFromFile on its fileId.
- getSchema → understand the target fields.
- proposeImport → stages one process document, fully mapped and scored server-side (Typesafe); you NEVER author values, mappings, or scores. On a formVersion/record page call proposeImport(fileId) with no selection — the WHOLE document is staged: header fields, entity references, and every table section (assigned to a target table on the bound document). On an entity page call proposeImport(fileId, chunkId|groupId) to stage that table section as records; flag low-confidence column mappings to the user.
- applyToDocument(fileId, processIndex) → applies the staged document to the bound form version or record in ONE approved step covering everything it contains — fields, references, and all assigned tables. The user reviews and edits the staged mapping in the Mapping Review UI during approval; never write extracted fields or rows yourself.
- applyToRecords(fileId, processIndex) → creates records on the bound entity (status Pending) from an entity-page staging.

For high-volume parts resolution (PO-style files where each row should map to a real referenced part):
- extractFromFile(fileId) → inspect columns.
- findPartsBatch(fileId, seedMapping) with any of nameColumn/skuColumn/descriptionColumn → resolves every row, writes a PartResolutionsDocument JSON inside the file's folder, returns { ok, summary }. The candidates do not enter the conversation; only the summary does.
- applyToDocument(fileId, processIndex, useResolutions: true) → applies the staged document, overlaying the parts resolutions on top of the staged reference resolutions.

setTableRow is ONLY for a single row the user explicitly asked to add or edit (getTableRows finds the rowId to patch). NEVER use it for rows that came from a file or extraction — stage those with proposeImport and apply via applyToDocument.

The same fileId addresses a file across every tool and every turn — there is no request-scoped pointer to expire. Never call extractFromFile just to obtain a usable handle; re-extract only to retry a corrupted parse.`;

const AGENTIC_PDF_INTAKE_GUIDANCE = `

For PDFs, call startPdfIntake(fileId) instead of extractFromFile — extractFromFile refuses them, and this replaces the PDF/DOCX/PPTX line above for PDFs:
- startPdfIntake runs PDF intake to the end against the open form and returns { jobId, status, route, pages, flaggedCells, failedPages, alreadyRead, href }.
- Never call proposeImport or applyToDocument for that PDF; the user reviews the schedule at href.
- Reply in one short message with pages read, flagged cells and failed pages, linking [Open the schedule review](href) with the returned href exactly.
- When alreadyRead is true, say this PDF was already read and link that review.
- CSV, XLSX and other files still go extractFromFile → proposeImport → applyToDocument.`;

export function buildAgentContextPrompt(ctx?: AgentContext): string {
    if (!ctx) return '';
    const parts: string[] = [];
    const { type, ids } = ctx.page;
    switch (type) {
        case 'formVersion':
            if (ids.formId) {
                parts.push(`\nCurrent context: User is viewing a form version.`);
                parts.push(`- Form ID: ${ids.formId}`);
                if (ids.formVersionId) {
                    parts.push(`- Form Version ID: ${ids.formVersionId}`);
                }
                parts.push(`When the user asks about "this form" or "the form", the harness binds these IDs automatically.`);
            }
            break;
        case 'home':
        case 'forms': {
            const where = type === 'home' ? 'the home page' : 'the forms list page';
            parts.push(`\nCurrent context: User is on ${where}.`);
            parts.push(`Call listForms to see the forms they can access; call navigate to open one.`);
            break;
        }
        case 'entity':
            if (ids.entityId) {
                parts.push(`\nCurrent context: User is on an entity page.`);
                parts.push(`- Entity ID: ${ids.entityId}`);
            }
            break;
        case 'record':
            if (ids.recordId) {
                parts.push(`\nCurrent context: User is viewing a record.`);
                if (ids.entityId) parts.push(`- Entity ID: ${ids.entityId}`);
                parts.push(`- Record ID: ${ids.recordId}`);
            }
            break;
        case 'settingsForms':
            parts.push(`\nCurrent context: User is on the form-type settings page.`);
            break;
        case 'settingsRecords':
            parts.push(`\nCurrent context: User is on the entity-schema settings page.`);
            break;
        case 'settingsTables':
            parts.push(`\nCurrent context: User is on the table-type settings page.`);
            break;
        case 'settingsWorkflows':
            parts.push(`\nCurrent context: User is on the workflows settings page.`);
            break;
        case 'fallback':
            parts.push(`\nCurrent context: User is on an unrecognized page; only navigation, identity, and read-only tools are available.`);
            break;
        default: {
            const _exhaustive: never = type;
            return _exhaustive;
        }
    }
    if (ctx.schema) {
        const rendered = renderSchema(ctx.schema);
        if (rendered) parts.push(rendered);
    }
    const signifyEnabled = ctx.toggles.signifyAuthorized === true;
    parts.push(
        `\nSignify lookups for this session: ${signifyEnabled ? 'enabled' : 'disabled'}. If a task would benefit from Signify and it is currently disabled, ask the operator to enable it — do not call Signify-only tools while disabled.`,
    );
    return parts.join('\n');
}

export function buildAgenticSystemPrompt(
    baseSystem: string,
    objective: string,
    maxIterations: number,
    tools: AgentToolDefinition[] = [],
): string {
    const extraction = tools.some((t) => EXTRACTION_TOOL_NAMES.has(t.name))
        ? AGENTIC_EXTRACTION_GUIDANCE
        : '';
    const pdfIntake = tools.some((t) => t.name === 'startPdfIntake') ? AGENTIC_PDF_INTAKE_GUIDANCE : '';
    return `${baseSystem}

You are operating in agentic mode with an objective to achieve autonomously.

OBJECTIVE: ${objective}

RULES:
- Work toward the objective step by step using the available tools.
- You MUST call the "finish" tool when done.
- If you cannot make progress, call finish with status "failure".
- You have a maximum of ${maxIterations} iterations.${extraction}${pdfIntake}`;
}
